import { useNavigate } from 'react-router-dom'

import { useAdminApiClient } from '../api/AdminApiClientContext'
import { queryKeys } from '../api/queryKeys'
import type {
  DeliveryProblem,
  DeliveryProblemListResponse,
} from '../api/types'
import { CodeChip } from '../components/CodeChip'
import {
  DataTable, type ColumnConfig,
} from '../components/DataTable'
import { PaginationControl } from '../components/PaginationControl'
import { RelativeTime } from '../components/RelativeTime'
import { StatusBadge } from '../components/StatusBadge'
import { useCursorPaginatedQuery } from '../hooks/useCursorPaginatedQuery'

function rowAge(row: DeliveryProblem): number {
  // Use the most recent useful timestamp on the record so the "age"
  // column means "time since the system last touched this delivery".
  return row.sent_time || row.created_time || 0
}

export function DeliveryProblemListPage() {
  const client = useAdminApiClient()
  const navigate = useNavigate()
  // Cursor-paginated to match the rest of the operator queues
  // (review_comments M2, HEAD 47a2b09). Page 1 fetches with no
  // cursor; subsequent pages forward `next_cursor` (a delivery_id).
  const result = useCursorPaginatedQuery<DeliveryProblem>({
    queryKey: queryKeys.deliveryProblems(),
    fetchPage: (cursor) => {
      const search = new URLSearchParams({ limit: '50' })
      if (cursor) search.set('cursor', cursor)
      return client.get<DeliveryProblemListResponse>(
        `/r/admin/deliveries/problems?${search.toString()}`,
      )
    },
  })

  const columns: ColumnConfig<DeliveryProblem>[] = [
    {
      key: 'status',
      header: 'Status',
      cell: (row) => <StatusBadge kind="delivery" value={row.status} />,
    },
    {
      key: 'recipient_name',
      header: 'Recipient',
      cell: (row) => row.recipient_name || <span className="text-stone-400">—</span>,
    },
    {
      key: 'recipient_domain',
      header: 'Recipient domain',
      cell: (row) => <CodeChip>{row.recipient_email_domain}</CodeChip>,
    },
    {
      key: 'attempt_count',
      header: 'Attempts',
      align: 'right',
      cell: (row) => row.attempt_count,
    },
    {
      key: 'last_error',
      header: 'Last error',
      cell: (row) => row.last_error
        ? <span className="text-xs text-red-700">{row.last_error}</span>
        : <span className="text-xs text-stone-400">—</span>,
    },
    {
      key: 'age',
      header: 'Age',
      cell: (row) => {
        const t = rowAge(row)
        return t
          ? <RelativeTime timestamp={t} />
          : <span className="text-xs text-stone-400">—</span>
      },
    },
  ]

  return (
    <div className="space-y-4 p-6">
      <header className="flex items-baseline justify-between">
        <h1 className="text-xl font-semibold text-stone-900">
          Delivery problems
        </h1>
        <span className="text-xs text-stone-500">
          Read-only — manual revoke arrives in Wave 2.
        </span>
      </header>
      <DataTable<DeliveryProblem>
        caption="Deliveries flagged for operator review"
        columns={columns}
        rows={result.flatItems}
        rowKey={(row) => row.delivery_id}
        isLoading={result.isLoading}
        isError={result.isError}
        errorMessage={result.error?.message ?? 'Failed to load queue.'}
        emptyTitle="No delivery problems right now"
        emptyDescription="Permanent failures and stale attempts will appear here as Mailgun reports them."
        onRowClick={(row) => navigate(`/deliveries/${row.delivery_id}`)}
        footer={
          <PaginationControl
            loadedCount={result.flatItems.length}
            hasMore={result.hasMore}
            isFetchingMore={result.isFetchingMore}
            onLoadMore={() => result.loadMore()}
          />
        }
      />
    </div>
  )
}
